import { useEffect, useEffectEvent, useRef, useState, type ReactNode, type RefObject } from 'react';
import { SparkleIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { useFocusTrap } from '../../shared/focus_trap.js';
import { useStableCallback } from '../../shared/use_stable_callback.js';
import './checkout_shell.css';

export interface CheckoutShellCopy {
  close: string;
  /** A voz do agente acima do form, só na fase `ready`. */
  agentLine: string;
  loading: string;
  loadingAria: string;
  error: string;
  /** O "Tentar de novo" da fase `error`, quando o host a sabe refazer. */
  retry: string;
}

export interface CheckoutShellProps {
  phase: 'loading' | 'ready' | 'error';
  title: string;
  /** Contexto do topbar ("Voo · Rio → São Paulo · 17 – 22 set"). */
  chip: string;
  copy: CheckoutShellCopy;
  /** Coluna lateral ("Sua viagem"): montada do loading ao ready. */
  side: ReactNode;
  /** Coluna principal, só na fase `ready`. */
  children: ReactNode;
  /** A barra do celular (#5150): total e ação ao alcance enquanto a coluna
   *  lateral está fora de cena. Recebe `reveal`, que rola até a coluna — o
   *  caminho quando a ação ainda está travada e o motivo mora lá. Abaixo da
   *  régua `wide` e só na fase `ready`; ausente ⇒ sem barra. */
  dock?: (reveal: () => void) => ReactNode;
  /** Refaz a leitura que pôs a casca em `error` (#6644: o form corporativo que
   *  caiu). Ausente ⇒ o erro é definitivo e só o ✕ sai. */
  onRetry?: () => void;
  onClose: () => void;
}

/** O chrome do checkout: takeover modal de tela cheia, topbar, feedback de fase e a
 *  grade de duas colunas (form à esquerda, resumo à direita a partir da régua
 *  `wide`). Escape fecha. */
export function CheckoutShell({
  phase,
  title,
  chip,
  copy,
  side,
  children,
  dock,
  onRetry,
  onClose,
}: Readonly<CheckoutShellProps>) {
  // Takeover de tela cheia é modal DE VERDADE: sem o trap, Tab depois do último
  // controle cairia no conteúdo coberto.
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);
  // O shell monta ao abrir e desmonta ao fechar. O opener é capturado ANTES de
  // semear o foco no painel (no mesmo efeito: efeitos rodam em ordem de
  // declaração) e recebe o foco de volta no unmount. Semear o foco NO PAINEL (não
  // no ✕): sem isso o foco fica no opener coberto e o trap nunca engata — Tab
  // seguiria a ordem do documento por baixo do overlay. Focar o dialog faz o
  // leitor de tela anunciar o título primeiro. O evento lê o `onClose` mais
  // recente sem recapturar.
  const close = useEffectEvent(() => {
    onClose();
  });
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus();
      }
    };
  }, []);

  // Marco no FIM da coluna lateral: é onde moram as ações e as pendências do
  // resumo no celular. A barra só cede a vez quando ele aparece — um resumo mais
  // alto que a tela já em cena pelo topo ainda deixa o Solicitar fora de alcance.
  const sideEndRef = useRef<HTMLDivElement>(null);
  const sideEndInView = useInView(panelRef, sideEndRef, dock !== undefined && phase === 'ready');
  // Stable, and handed to `dock` during render: it only runs on a press.
  const reveal = useStableCallback(() => {
    sideEndRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  });
  const typing = useTyping(panelRef);
  const dockHidden = sideEndInView === true || typing;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="pcd-overlay scrollbar-rail [scrollbar-gutter:auto]!"
      data-dock={(dock !== undefined && phase === 'ready') || undefined}
      ref={panelRef}
      tabIndex={-1}
    >
      <div className="pcd-topbar">
        <button type="button" className="pcd-close" onClick={onClose} aria-label={copy.close}>
          ✕
        </button>
        <h2 className="pcd-title">{title}</h2>
        <span className="pcd-chip">{chip}</span>
      </div>
      <div className="pcd-body">
        {phase === 'ready' && (
          <p className="pcd-agent-line">
            <SparkleIcon size={16} weight="fill" aria-hidden />
            <span>{copy.agentLine}</span>
          </p>
        )}
        {phase === 'error' ? (
          <div className="pcd-error flex flex-col items-start gap-3" role="alert">
            <p>{copy.error}</p>
            {onRetry && (
              <Button size="sm" variant="outline" onClick={onRetry}>
                {copy.retry}
              </Button>
            )}
          </div>
        ) : (
          // UMA grade pros dois estados: a coluna lateral fica MONTADA do loading
          // ao ready (trocar de instância soltava o nó de quem já o tinha nas
          // mãos — flicker real, teste flaky); só a coluna do form troca
          // skeleton ↔ campos.
          <div className="pcd-grid">
            <div className="pcd-col pcd-col--main">
              {phase === 'loading' ? <CheckoutSkeleton copy={copy} /> : children}
            </div>
            <div className="pcd-col pcd-col--side">
              {side}
              {dock && <div className="pcd-side-end" ref={sideEndRef} aria-hidden="true" />}
            </div>
          </div>
        )}
      </div>
      {/* Montada desde a primeira medida: sumir é um deslize, não um desmonte. Fora
          de cena fica `inert` — nem foco nem leitor de tela chegam a ela. */}
      {dock && phase === 'ready' && sideEndInView !== undefined && (
        <div
          className="pcd-dock wide:hidden"
          data-hidden={dockHidden || undefined}
          inert={dockHidden}
        >
          {dock(reveal)}
        </div>
      )}
    </div>
  );
}

/** Campos que abrem o teclado virtual. */
const TEXT_ENTRY =
  'textarea, [contenteditable]:not([contenteditable="false"]), input:not([type="button"], [type="checkbox"], [type="radio"], [type="submit"], [type="reset"], [type="file"], [type="range"], [type="color"])';

/** Foco num campo de texto dentro do takeover. Com o teclado aberto, o iOS rola
 *  o documento (não o overlay) e a barra `fixed` sobe junto, cobrindo o campo
 *  que se digita; enquanto se digita ela sai de cena e volta no blur. */
function useTyping(rootRef: RefObject<HTMLElement | null>): boolean {
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const update = (target: EventTarget | null) => {
      setTyping(target instanceof Element && target.matches(TEXT_ENTRY));
    };
    const onFocusIn = (event: FocusEvent) => {
      update(event.target);
    };
    const onFocusOut = (event: FocusEvent) => {
      update(event.relatedTarget);
    };
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);
    return () => {
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
    };
  }, [rootRef]);
  return typing;
}

/** O alvo em cena no rolo do takeover. `undefined` até a primeira medida — a
 *  barra nem monta, para não piscar; sem `IntersectionObserver` (jsdom sem
 *  polyfill) fica assim, e o resumo de sempre segue no fim da coluna. */
function useInView(
  rootRef: RefObject<HTMLElement | null>,
  targetRef: RefObject<HTMLElement | null>,
  enabled: boolean,
): boolean | undefined {
  const [inView, setInView] = useState<boolean>();
  useEffect(() => {
    const target = targetRef.current;
    const root = rootRef.current;
    if (!enabled || !target || !root || typeof IntersectionObserver !== 'function')
      return undefined;
    // A linha de corte é o topo da barra, não o pé da tela: o CSS reserva a
    // altura dela em `scroll-padding-bottom` (com o safe-area já resolvido). A
    // folga de 4px faz a barra ceder a vez quando o `reveal` assenta o fim ali.
    const dockHeight = Number.parseFloat(getComputedStyle(root).scrollPaddingBottom) || 0;
    const observer = new IntersectionObserver(
      (entries) => {
        setInView(entries.some((entry) => entry.isIntersecting));
      },
      { root, rootMargin: `0px 0px -${String(Math.max(0, dockHeight - 4))}px 0px` },
    );
    observer.observe(target);
    return () => {
      observer.disconnect();
      // Uma nova fase `ready` (o INIT pode voltar a `loading`) espera a medida
      // nova: a antiga montaria a barra com `data-hidden`/`inert` vencidos.
      setInView(undefined);
    };
  }, [enabled, rootRef, targetRef]);
  return inView;
}

/** Fantasmas com a MESMA anatomia dos dois cartões reais (viajante + motivo), pra
 *  tela não "pular" quando o form chega. */
function CheckoutSkeleton({ copy }: { readonly copy: CheckoutShellCopy }) {
  return (
    // div[role=status], não <output>: skeleton é conteúdo de bloco e <output> só
    // aceita phrasing content (WHATWG).
    <div className="pcd-skel" role="status" aria-busy="true" aria-label={copy.loadingAria}>
      <span className="pcd-sr-only">{copy.loading}</span>
      <div className="pcd-skel-card" aria-hidden="true">
        <div className="pcd-skel-head">
          <span className="pcd-skel-avatar" />
          <span className="pcd-skel-line pcd-skel-line--w40" />
        </div>
        <div className="pcd-skel-row">
          <span className="pcd-skel-field" />
          <span className="pcd-skel-field" />
          <span className="pcd-skel-field" />
        </div>
        <span className="pcd-skel-line pcd-skel-line--w60" />
      </div>
      <div className="pcd-skel-card" aria-hidden="true">
        <span className="pcd-skel-line pcd-skel-line--w40" />
        <span className="pcd-skel-field" />
        <span className="pcd-skel-field pcd-skel-field--tall" />
      </div>
    </div>
  );
}
